import React from "react";
import { BookOpen } from "lucide-react";

export function MetricCard({ label, value, meta }) {
  return (
    <div className="bg-gradient-to-br from-white to-[#f8fbff] border border-[#e6edf8] rounded-[18px] p-4 shadow-sm">
      <div className="text-[11px] text-[#6b7280] mb-1.5">{label}</div>
      <div className="text-2xl font-bold text-[#1565c0] leading-tight mb-1">{value}</div>
      {meta && <div className="text-[12px] text-[#607086] leading-[1.45]">{meta}</div>}
    </div>
  );
}

export function SoftPanel({ title, children, className = "" }) {
  return (
    <div className={`bg-gradient-to-br from-white to-[#f9fbff] border border-[#e6edf8] rounded-[18px] p-4 ${className}`}>
      {title && <h4 className="m-0 mb-2 text-[15px] font-bold text-[#1565c0]">{title}</h4>}
      {children}
    </div>
  );
}

export function InfoBanner({ children }) {
  return (
    <div className="bg-gradient-to-br from-[#fff4e8] to-[#eef5ff] border border-[#e8d3bc] rounded-[18px] p-4 text-[13px] text-[#374151] leading-[1.8]">
      {children}
    </div>
  );
}

export function SectionDivider() {
  return (
    <div
      className="w-full my-[18px]"
      style={{ height: 1, background: "linear-gradient(90deg,transparent,#dce6f2,transparent)" }}
    />
  );
}

export function EmptyState({ children }) {
  return (
    <div className="text-center py-8 px-4 text-[13px] text-[#6b7280] border border-dashed border-[#cfdaea] bg-[#fbfdff] rounded-[16px]">
      {children}
    </div>
  );
}

export function Tag({ children, color = "default", className = "" }) {
  const colors = {
    default: "bg-white border-[#d9e7f7] text-[#1565c0]",
    orange: "bg-[#fff4e8] border-[#ffd8b5] text-[#b85d00]",
    green: "bg-[#eefbf1] border-[#cce8d5] text-[#22784b]",
    yellow: "bg-[#fff9e8] border-[#f2e1a8] text-[#9f7900]",
    red: "bg-[#fff4f4] border-[#f0cccc] text-[#b63a3a]",
    purple: "bg-[#f5f0ff] border-[#dacafc] text-[#6a47c7]",
  };
  return (
    <span className={`inline-flex items-center px-[10px] py-[7px] rounded-full border text-[11px] font-semibold ${colors[color] || colors.default} ${className}`}>
      {children}
    </span>
  );
}

export function TableChip({ children, color = "default" }) {
  const colors = {
    default: "bg-[#eef5ff] border-[#d7e6fa] text-[#1565c0]",
    orange: "bg-[#fff4e8] border-[#ffd8b5] text-[#b85d00]",
    green: "bg-[#eefbf1] border-[#cce8d5] text-[#22784b]",
    purple: "bg-[#f5f0ff] border-[#dacafc] text-[#6a47c7]",
  };
  return (
    <span className={`inline-block px-2 py-[4px] rounded-full text-[11px] font-semibold border mx-0.5 ${colors[color] || colors.default}`}>
      {children}
    </span>
  );
}

export function InputLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block text-[12px] font-semibold text-[#475569] mb-[7px]">
      {children}
    </label>
  );
}

export function HelpText({ children, className = "" }) {
  return <div className={`text-[11px] text-[#6b7280] mt-1.5 leading-[1.5] ${className}`}>{children}</div>;
}

export function FormInput({ id, type = "text", value, onChange, placeholder, readOnly, min, className = "" }) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      min={min}
      className={`w-full px-3 py-[11px] border border-[#cad7e7] rounded-xl bg-white font-[Poppins,sans-serif] text-[13px] text-[#1f2937] outline-none transition-all focus:border-[#1565c0] focus:ring-2 focus:ring-[#1565c012] ${readOnly ? "bg-[#f9fafb]" : ""} ${className}`}
    />
  );
}

export function FormSelect({ id, value, onChange, children, multiple, size, disabled, className = "" }) {
  return (
    <select
      id={id}
      value={value}
      onChange={onChange}
      multiple={multiple}
      size={size}
      disabled={disabled}
      className={`w-full px-3 py-[11px] border border-[#cad7e7] rounded-xl bg-white font-[Poppins,sans-serif] text-[13px] text-[#1f2937] outline-none transition-all focus:border-[#1565c0] focus:ring-2 focus:ring-[#1565c012] ${multiple ? "min-h-[108px] py-2" : ""} ${className}`}
    >
      {children}
    </select>
  );
}

export function FormTextarea({ id, value, onChange, placeholder, rows, disabled, className = "" }) {
  return (
    <textarea
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows || 4}
      disabled={disabled}
      className={`w-full px-3 py-[11px] border border-[#cad7e7] rounded-xl bg-white font-[Poppins,sans-serif] text-[13px] text-[#1f2937] outline-none transition-all focus:border-[#1565c0] focus:ring-2 focus:ring-[#1565c012] min-h-[82px] resize-y leading-[1.7] ${className}`}
    />
  );
}

export function BtnPrimary({ onClick, children, type = "button", disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="border-none rounded-xl px-[18px] py-[11px] font-[Poppins,sans-serif] text-[13px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center gap-2 bg-[#f57c00] text-white hover:bg-[#db6f00] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export function BtnSecondary({ onClick, children, type = "button", disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="border-none rounded-xl px-[18px] py-[11px] font-[Poppins,sans-serif] text-[13px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center gap-2 bg-[#1565c0] text-white hover:bg-[#0f4f98] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export function BtnLight({ onClick, children, type = "button", disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="rounded-xl px-[18px] py-[11px] font-[Poppins,sans-serif] text-[13px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center gap-2 bg-white text-[#1565c0] border border-[#d7e5f7] hover:shadow-sm hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

export function BtnSuccess({ onClick, children, type = "button", disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="border-none rounded-xl px-[18px] py-[11px] font-[Poppins,sans-serif] text-[13px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center gap-2 bg-[#1f8a46] text-white hover:-translate-y-px disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function BtnDanger({ onClick, children, type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className="rounded-xl px-[14px] py-[10px] font-[Poppins,sans-serif] text-[11px] font-semibold cursor-pointer transition-all inline-flex items-center justify-center gap-2 bg-[#fff4f4] text-[#d32f2f] border border-[#f1caca] hover:-translate-y-px"
    >
      {children}
    </button>
  );
}

export function BtnRow({ children, className = "" }) {
  return <div className={`flex flex-wrap gap-2.5 mt-4 ${className}`}>{children}</div>;
}

export function ModuleCard({ id, children }) {
  return (
    <section
      id={id}
      className="p-[8px] mb-5"
      style={{ scrollMarginTop: 100 }}
    >
      {children}
    </section>
  );
}

export function ModuleHeader({ step, title, description, statusId, statusDefault, statusMeta, showStatus = true, showIcon = false }) {
  return (
    <div className="mb-4">
      {showIcon ? (
        <div className="w-14 h-14 rounded-full bg-[#D67C2C] flex items-center justify-center mb-4 mx-auto">
          <BookOpen className="w-7 h-7 text-white" />
        </div>
      ) : null}

      <div className="flex justify-between items-start gap-[18px] flex-wrap">
        <div className="max-w-[940px]">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold text-[#f57c00] bg-[#fff4e8] border border-[#ffd9b4] rounded-full px-[10px] py-[6px] mb-2.5 uppercase tracking-[0.3px]">
            {step}
          </div>
          <h3 className="m-0 mb-1.5 text-[22px] leading-[1.3] text-[#1565c0] font-bold">{title}</h3>
          {description && <p className="m-0 text-[13px] text-[#6b7280] leading-[1.8]">{description}</p>}
        </div>

        {showStatus ? (
          <div className="min-w-[240px] bg-gradient-to-br from-white to-[#f7fbff] border border-[#e4edf8] rounded-[18px] p-[14px]">
            <div className="text-[11px] text-[#6b7280] mb-1.5">Current Status</div>
            <div id={statusId} className="text-[18px] text-[#1565c0] font-bold mb-[5px]">{statusDefault}</div>
            {statusMeta && <div className="text-[12px] text-[#607086] leading-[1.5]">{statusMeta}</div>}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TableWrap({ children }) {
  return (
    <div className="w-full overflow-x-auto border border-[#e4edf8] rounded-[16px] bg-white">
      <table className="w-full border-collapse min-w-[960px] text-[13px]">
        {children}
      </table>
    </div>
  );
}

export function Th({ children, style }) {
  return (
    <th style={style} className="border border-[#d9e4f0] px-[10px] py-[11px] text-left align-top bg-[#f2f7fd] text-[#1565c0] font-semibold text-[13px]">
      {children}
    </th>
  );
}

export function Td({ children, style, className = "" }) {
  return (
    <td style={style} className={`border border-[#d9e4f0] px-[10px] py-[11px] text-left align-top bg-white text-[13px] ${className}`}>
      {children}
    </td>
  );
}

export function Grid2({ children, className = "" }) {
  return <div className={`grid grid-cols-2 gap-4 ${className}`}>{children}</div>;
}

export function Grid3({ children, className = "" }) {
  return <div className={`grid grid-cols-3 gap-4 ${className}`}>{children}</div>;
}

export function Grid4({ children, className = "" }) {
  return <div className={`grid grid-cols-4 gap-3.5 ${className}`}>{children}</div>;
}
